import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getFrameworks, getFieldNotes } from "@/lib/api";
import { track } from "@/lib/analytics";
import { amp } from "@/lib/amp";

function truncate(text, max = 130) {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}\u2026` : text;
}

/**
 * Cross-links the resource library as crawlable internal links. Rendered inside
 * .exec-scope article pages (Field Notes, Frameworks).
 */
export function RelatedResources({
  currentType,
  currentSlug,
  topic,
  title = "Related reading",
  limit = 3,
}) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let active = true;
    Promise.all([
      getFrameworks().catch(() => []),
      getFieldNotes().catch(() => []),
    ]).then(([fw, fn]) => {
      if (!active) return;
      const frameworks = (fw || [])
        .filter((f) => !f.links_to_tool && f.slug)
        .map((f) => ({
          type: "framework",
          slug: f.slug,
          to: `/resources/frameworks/${f.slug}`,
          kicker: f.kicker || "Framework",
          title: f.title,
          blurb: f.description || f.summary || "",
          topic: f.topic,
        }));
      const notes = (fn || [])
        .filter((n) => !n.canonical_url && n.slug)
        .map((n) => ({
          type: "note",
          slug: n.slug,
          to: `/resources/field-notes/${n.slug}`,
          kicker: "Field Note",
          title: n.title,
          blurb: n.excerpt || "",
          topic: n.topic,
        }));
      const pool = [...frameworks, ...notes].filter(
        (it) => !(it.type === currentType && it.slug === currentSlug)
      );
      const sameTopic = pool.filter((it) => topic && it.topic === topic);
      const rest = pool.filter((it) => !(topic && it.topic === topic));
      if (active) setItems([...sameTopic, ...rest].slice(0, limit));
    });
    return () => {
      active = false;
    };
  }, [currentType, currentSlug, topic, limit]);

  if (items.length === 0) return null;

  return (
    <section
      className="mt-14 pt-10 border-t border-[color:var(--rule)]"
      data-testid="related-resources"
      aria-label={title}
    >
      <p className="exec-eyebrow">{title}</p>
      <ul className="mt-3 divide-y divide-[color:var(--rule)]">
        {items.map((it) => (
          <li key={`${it.type}-${it.slug}`}>
            <Link
              to={it.to}
              data-testid={`related-${it.type}-${it.slug}`}
              onClick={() =>
                track("related_resource_click", {
                  to: it.to,
                  from_type: currentType,
                  from_slug: currentSlug,
                })
              }
              className="group flex items-start justify-between gap-5 py-5"
            >
              <div className="min-w-0">
                <p className="exec-eyebrow">{amp(it.kicker)}</p>
                <h3 className="font-light text-[20px] mt-1.5 leading-[1.2] text-[color:var(--ink)]">
                  {it.title}
                </h3>
                {it.blurb && (
                  <p className="exec-serif text-[15.5px] text-[color:var(--lead-ink)] mt-1.5">
                    {truncate(it.blurb)}
                  </p>
                )}
              </div>
              <ArrowRight
                className="h-5 w-5 mt-1 shrink-0 text-[color:var(--copper)] transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
